import { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Save, Film } from 'lucide-react';
import { videoStore } from '../lib/videos';
import { adminStore } from '../lib/admin';
import { formatDate } from '../lib/utils';
import type { VideoMeta, SessionMeta } from '../types';

interface SessionPlayerProps {
  isAdmin: boolean;
}

export function SessionPlayer({ isAdmin }: SessionPlayerProps) {
  const [videos, setVideos] = useState<VideoMeta[]>([]);
  const [session, setSession] = useState<SessionMeta>(() => adminStore.getSession());
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [titleDraft, setTitleDraft] = useState(session.title);
  const [notesDraft, setNotesDraft] = useState(session.notes);
  const urlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedId = session.videoId ?? videos[0]?.id ?? null;

  const reload = async () => {
    const list = await videoStore.list();
    setVideos(list);
  };

  useEffect(() => {
    reload();
  }, []);

  // Resolve the selected video blob into an object URL.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      if (!selectedId) {
        setVideoUrl(null);
        return;
      }
      const blob = await videoStore.getBlob(selectedId);
      if (!cancelled && blob) {
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setVideoUrl(url);
      } else if (!cancelled) {
        setVideoUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, videos]);

  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.includes('mp4') && !file.name.toLowerCase().endsWith('.mp4')) {
      alert('Please upload an MP4 video file.');
      return;
    }
    setUploading(true);
    try {
      const meta = await videoStore.save(file);
      await reload();
      const next = adminStore.setSession({ videoId: meta.id, title: session.title || meta.name.replace(/\.mp4$/i, '') });
      setSession(next);
      setTitleDraft(next.title);
      setNotesDraft(next.notes);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSelect = (id: string) => {
    setSession(adminStore.setSession({ videoId: id }));
  };

  const handleSaveMeta = () => {
    setSession(adminStore.setSession({ title: titleDraft.trim(), notes: notesDraft.trim() }));
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this video?')) return;
    await videoStore.remove(id);
    await reload();
    if (session.videoId === id) {
      const remaining = videos.filter(v => v.id !== id);
      setSession(adminStore.setSession({ videoId: remaining[0]?.id ?? null }));
    }
  };

  const metaDirty = titleDraft !== session.title || notesDraft !== session.notes;

  return (
    <div className="oem-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-oem-navy">Selected Session</h3>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {videos.length > 1 && (
              <select
                value={selectedId ?? ''}
                onChange={(e) => handleSelect(e.target.value)}
                className="oem-input text-xs py-1.5"
              >
                {videos.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,.mp4"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="oem-button-outline flex items-center gap-1.5 text-xs py-1.5 px-3"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? 'Uploading…' : 'Upload MP4'}
            </button>
          </div>
        )}
      </div>

      {videoUrl ? (
        <div className="space-y-4">
          <video
            key={videoUrl}
            src={videoUrl}
            controls
            className="w-full rounded-lg bg-black aspect-video"
          />

          {isAdmin ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-oem-muted">Title</label>
                <input
                  type="text"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  placeholder="Session title…"
                  className="oem-input w-full"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-oem-muted">Notes</label>
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={2}
                  placeholder="Notes about this session…"
                  className="oem-input w-full resize-none"
                />
              </div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => selectedId && handleDelete(selectedId)}
                  className="flex items-center gap-1.5 text-xs text-status-escalation hover:underline"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete video
                </button>
                <button
                  onClick={handleSaveMeta}
                  disabled={!metaDirty}
                  className="oem-button flex items-center gap-1.5 text-xs py-1.5 px-3 disabled:opacity-40"
                >
                  <Save className="w-3.5 h-3.5" />
                  Save
                </button>
              </div>
            </div>
          ) : (
            (session.title || session.notes) && (
              <div>
                {session.title && <p className="text-sm font-medium text-oem-900">{session.title}</p>}
                {session.notes && <p className="text-sm text-oem-muted mt-1">{session.notes}</p>}
              </div>
            )
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-10 border border-dashed border-oem-200 rounded-lg text-oem-muted">
          <Film className="w-8 h-8 text-oem-300" />
          <p className="text-sm">{isAdmin ? 'Upload an MP4 session recording to get started.' : 'No session selected yet.'}</p>
          {isAdmin && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="oem-button-outline flex items-center gap-1.5 text-xs py-1.5 px-3"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? 'Uploading…' : 'Upload MP4'}
            </button>
          )}
        </div>
      )}

      {videos.length > 0 && (
        <p className="mt-3 text-xs text-oem-muted">
          {videos.length} video{videos.length !== 1 ? 's' : ''} stored locally
          {selectedId && videos.find(v => v.id === selectedId) && (
            <> · uploaded {formatDate(videos.find(v => v.id === selectedId)!.uploadedAt)}</>
          )}
        </p>
      )}
    </div>
  );
}
